import { FormSectionHandler } from '../entity.handler';
import { ClientResponse, IdName, ClientUpdateRequest, VendorResponse } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, ComboField, CheckListField, IdNameComboValueHandlers, IdNameArrayComboValueHandlers } from '../utils';
import { nullAccessDenied } from 'app/services';

interface EditorValues {
    mapSet?: IdName;
    availableDeviceTypes?: IdName[];
}

export class ClientSettingsSectionHandler implements FormSectionHandler<ClientResponse, EditorValues> {

    type = 'form';
    icon = 'cogs';
    bulk = true;
    readonly = this.admin.user.owner.type === 'client';

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    private properties = ['mapSet', 'availableDeviceTypes'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.CLIENT.SETTINGS.${key}`, values);
    }

    applyUpdate(entity: ClientResponse, payload: ClientUpdateRequest): Promise<ClientResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateClient(entity.id, payload)).then(client => {
            this.admin.clientUpdated(client);
            return client;
        });
    }

    mapEntityToEditor(entity: ClientResponse): Promise<EditorValues> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity, {});
        result.mapSet = result.mapSet || { id: 'default', name: 'Default' };
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: any, values: EditorValues): Promise<any> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, values, entity);
        if (result.mapSet && result.mapSet.id === 'default') {
            result.mapSet = null;
        }
        return Promise.resolve(result);
    }

    async getFields(entity: ClientResponse): Promise<FormBuilderField[]> {
        const vendor = this.readonly ? null : await this.admin.getCached(`getVendor/${this.ownerId}`, api => api.accounts.getVendor(this.ownerId)).catch(nullAccessDenied);

        return Promise.all([
            this.getAvailableDeviceTypes(vendor),
            this.getAvailableMapSets(vendor),
        ]).then(([deviceTypeValues, mapSetValues]) => {
            deviceTypeValues.sort((a, b) => a.value.localeCompare(b.value));
            return [
                ComboField('mapSet', this.translate('FIELDS.MAPSET'), true, [{ key: 'default', value: 'Default' }, ...mapSetValues], IdNameComboValueHandlers),
                CheckListField('availableDeviceTypes', this.translate('FIELDS.DEVICETYPES'), false, deviceTypeValues, IdNameArrayComboValueHandlers),
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, ['mapSet', 'availableDeviceTypes'], fields));
    }


    async getAvailableDeviceTypes(vendor: VendorResponse): Promise<{ key: string, value: string }[]> {
        if (vendor && vendor.availableDeviceTypes && vendor.availableDeviceTypes.length > 0) {
            const parentDeviceTypes = await this.admin.getCached(`listDeviceTypes/${vendor.id}/false/`, api => api.entities.listDeviceTypes(vendor.id, false, 0, 1000));
            return [
                ...(parentDeviceTypes?.items?.filter(x => !x.parent).map(x => ({ key: x.id, value: x.name })) || []),
                ...(vendor?.availableDeviceTypes?.map(x => ({ key: x.id, value: x.name })) || []),
            ];
        }
        const deviceTypes = vendor ? await this.admin.getCached(`listDeviceTypes/${vendor.id}`, api => api.entities.listDeviceTypes(vendor.id, true, 0, 1000)) : null;
        return deviceTypes?.items?.filter(x => !x.parent).map(x => ({ key: x.id, value: x.name })) || [];
    }

    async getAvailableMapSets(vendor: VendorResponse): Promise<{ key: string, value: string }[]> {
        const mapSets = vendor ? await this.admin.getCached(`listMapSets/${vendor.id}/0/100/name/state!=deleted`, api => api.entities.listMapSets(vendor.id, 0, 100, 'name', 'state!=deleted')).catch(nullAccessDenied) : null;
        return [
            ...(mapSets?.items?.map(x => ({ key: x.id, value: x.name })) || []),
            ...(vendor?.availableMapSets?.map(x => ({ key: x.id, value: x.name })) || []),
        ].filter(x => x).sort((a, b) => a.value.localeCompare(b.value));
    }
}
